import { useState } from 'react'
import dayjs from 'dayjs'
import RelativeTime from 'dayjs/plugin/relativeTime'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Monitor } from 'lucide-react'
import { Button } from '@/components/ui/button'
import Card from 'app/frontend/base/components/Card'
import { useDesktopTokens } from 'app/frontend/base/config/queries'
import { revokeDesktopToken } from 'app/frontend/base/api/user'
import { displayErrorToast } from 'app/frontend/base/helpers'

dayjs.extend(RelativeTime)

const UserSettingsDesktopSection = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { data: tokens, isPending } = useDesktopTokens()
  const [revokingId, setRevokingId] = useState<number>()

  const revoke = (id: number) => {
    setRevokingId(id)
    revokeDesktopToken(id)
      .then(() =>
        queryClient.invalidateQueries({ queryKey: ['desktopTokens'] }),
      )
      .catch((err) => displayErrorToast(err, t))
      .then(() => setRevokingId(undefined))
  }

  if (isPending) return null

  return (
    <div>
      <h4>Desktop client sessions</h4>
      <p>
        Desktop clients you logged in with can sync in-game data on your behalf.
        Revoke a session to log that client out.
      </p>

      {!tokens?.length ? (
        <p className='text-muted-foreground'>
          You are not logged in to any desktop client.
        </p>
      ) : (
        tokens.map((token) => (
          <Card
            key={token.id}
            className='py-3 px-4 my-2 flex items-center justify-between gap-2'
          >
            <div className='flex items-center gap-4 min-w-0'>
              <Monitor size={20} className='shrink-0' />
              <div className='min-w-0'>
                <div className='font-bold truncate'>
                  {token.name || 'Desktop client'}
                </div>
                <div className='text-sm text-muted-foreground'>
                  Logged in {dayjs(token.createdAt).fromNow()}
                  {token.lastUsedAt
                    ? `, last synced ${dayjs(token.lastUsedAt).fromNow()}`
                    : ''}
                </div>
              </div>
            </div>
            <Button
              variant='outline'
              loading={revokingId === token.id}
              onClick={() => revoke(token.id)}
            >
              Revoke
            </Button>
          </Card>
        ))
      )}
    </div>
  )
}

export default UserSettingsDesktopSection
